import { RouterProvider } from "@tanstack/react-router";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { toast } from "sonner";
import { router } from "./router";
import "./index.css";

const elem = document.getElementById("root")!;
const app = (
  <StrictMode>
    <RouterProvider router={router} />
  </StrictMode>
);

if (import.meta.hot) {
  const root = (import.meta.hot.data.root ??= createRoot(elem));
  root.render(app);
} else {
  createRoot(elem).render(app);
}

// --- PWA_FEATURE_START ---
// PWA head tags (injected via JS to avoid Bun bundler resolution)
function addLink(rel: string, href: string, attrs?: Record<string, string>) {
  const link = document.createElement("link");
  link.rel = rel;
  link.href = href;
  if (attrs) for (const [k, v] of Object.entries(attrs)) link.setAttribute(k, v);
  document.head.appendChild(link);
}
addLink("manifest", "/manifest.json");
addLink("apple-touch-icon", "/icons/icon-192.png");
addLink("icon", "/icons/icon.svg", { type: "image/svg+xml" });
addLink("stylesheet", "/fonts/fonts.css");

// Service Worker registration. In dev (localhost / 127.0.0.1) we
// aggressively unregister any existing SW and wipe its caches — the SW is
// cache-first for JS/CSS, so a stale SW from a prior production install
// will keep serving old bundles even on hard refresh. Detection is based
// on hostname instead of `import.meta.env.PROD` because Bun's dev server
// doesn't always set that flag, and a wrong env read here is the exact
// thing that traps a stale SW in place.
const isDev =
  typeof window !== "undefined" &&
  (location.hostname === "localhost" ||
    location.hostname === "127.0.0.1" ||
    location.hostname.endsWith(".local"));

if ("serviceWorker" in navigator) {
  if (isDev) {
    // Reload once after cleanup — the existing SW keeps controlling the
    // page until the next navigation; sessionStorage prevents a loop.
    (async () => {
      try {
        const regs = await navigator.serviceWorker.getRegistrations();
        const cacheKeys = await caches.keys();
        const hadSomething = regs.length > 0 || cacheKeys.length > 0;
        await Promise.all(regs.map((r) => r.unregister()));
        await Promise.all(cacheKeys.map((k) => caches.delete(k)));
        if (hadSomething && !sessionStorage.getItem("sw-dev-cleanup-done")) {
          sessionStorage.setItem("sw-dev-cleanup-done", "1");
          location.reload();
        }
      } catch (err) {
        console.warn("SW cleanup failed:", err);
      }
    })();
  } else {
    window.addEventListener("load", () => {
      navigator.serviceWorker
        .register("/sw.js")
        .then((reg) => {
          // Check for updates periodically (every 60 minutes)
          setInterval(() => reg.update(), 60 * 60 * 1000);

          reg.addEventListener("updatefound", () => {
            const newWorker = reg.installing;
            if (!newWorker) return;

            newWorker.addEventListener("statechange", () => {
              if (newWorker.state === "activated" && navigator.serviceWorker.controller) {
                // New version available — non-blocking toast instead of the
                // browser-native confirm(), which would freeze the page.
                toast("A new version is available", {
                  duration: Number.POSITIVE_INFINITY,
                  action: {
                    label: "Reload",
                    onClick: () => window.location.reload(),
                  },
                });
              }
            });
          });
        })
        .catch((err) => console.error("SW registration failed:", err));
    });
  }
}
// --- PWA_FEATURE_END ---
